import * as React from "react"
import { Check, ChevronsUpDown, X } from "lucide-react"
import { cn } from "@/lib/utils"
import { Button } from "@/components/ui/button"
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"
import { Badge } from "@/components/ui/badge"

export interface ComboboxItem {
  value: string
  label: string
}

interface ComboboxContextValue {
  items: ComboboxItem[]
  multiple?: boolean
  value: ComboboxItem | ComboboxItem[] | null
  onValueChange: (value: ComboboxItem | ComboboxItem[] | null) => void
  open: boolean
  setOpen: (open: boolean) => void
  searchValue: string
  setSearchValue: (value: string) => void
}

const ComboboxContext = React.createContext<ComboboxContextValue | null>(null)

function useCombobox() {
  const context = React.useContext(ComboboxContext)
  if (!context) {
    throw new Error("Combobox components must be used within Combobox")
  }
  return context
}

interface ComboboxProps {
  items: ComboboxItem[]
  multiple?: boolean
  value?: ComboboxItem | ComboboxItem[] | null
  defaultValue?: ComboboxItem | ComboboxItem[] | null
  onValueChange?: (value: ComboboxItem | ComboboxItem[] | null) => void
  children: React.ReactNode
}

export function Combobox({
  items,
  multiple = false,
  value: controlledValue,
  defaultValue,
  onValueChange,
  children,
}: ComboboxProps) {
  const [open, setOpen] = React.useState(false)
  const [searchValue, setSearchValue] = React.useState("")
  const [internalValue, setInternalValue] = React.useState<ComboboxItem | ComboboxItem[] | null>(
    defaultValue ?? (multiple ? [] : null)
  )

  const value = controlledValue !== undefined ? controlledValue : internalValue

  const handleValueChange = React.useCallback(
    (newValue: ComboboxItem | ComboboxItem[] | null) => {
      if (controlledValue === undefined) {
        setInternalValue(newValue)
      }
      onValueChange?.(newValue)
    },
    [controlledValue, onValueChange]
  )

  const contextValue = React.useMemo(
    () => ({
      items,
      multiple,
      value,
      onValueChange: handleValueChange,
      open,
      setOpen,
      searchValue,
      setSearchValue,
    }),
    [items, multiple, value, handleValueChange, open, searchValue]
  )

  return (
    <ComboboxContext.Provider value={contextValue}>
      <Popover open={open} onOpenChange={setOpen}>
        {children}
      </Popover>
    </ComboboxContext.Provider>
  )
}

interface ComboboxValueProps {
  children: (value: ComboboxItem | ComboboxItem[] | null) => React.ReactNode
}

export function ComboboxValue({ children }: ComboboxValueProps) {
  const { value } = useCombobox()
  return <>{children(value)}</>
}

interface ComboboxChipsProps {
  children: React.ReactNode
  className?: string
}

export function ComboboxChips({ children, className }: ComboboxChipsProps) {
  const { open, setOpen } = useCombobox()

  return (
    <PopoverTrigger asChild>
      <Button
        variant="outline"
        role="combobox"
        aria-expanded={open}
        className={cn(
          "w-full justify-between h-auto min-h-10 px-3 py-2",
          className
        )}
        onClick={() => setOpen(!open)}
      >
        <div className="flex flex-wrap gap-1 flex-1 items-center">
          {children}
        </div>
        <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
      </Button>
    </PopoverTrigger>
  )
}

interface ComboboxChipProps {
  children: React.ReactNode
  "aria-label": string
}

export function ComboboxChip({ children, "aria-label": ariaLabel }: ComboboxChipProps) {
  const { value, onValueChange, multiple } = useCombobox()

  const handleRemove = (e: React.MouseEvent) => {
    e.stopPropagation()
    if (!multiple || !Array.isArray(value)) return

    const itemToRemove = value.find((item) => item.label === children)
    if (!itemToRemove) return

    const newValue = value.filter((item) => item.value !== itemToRemove.value)
    onValueChange(newValue.length > 0 ? newValue : null)
  }

  return (
    <Badge
      variant="secondary"
      className="gap-1 pr-1 font-normal"
      aria-label={ariaLabel}
    >
      {children}
      <span
        role="button"
        tabIndex={0}
        className="ml-1 ring-offset-background rounded-full outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 cursor-pointer"
        onClick={handleRemove}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            handleRemove(e as unknown as React.MouseEvent)
          }
        }}
        aria-label={`Remove ${ariaLabel}`}
      >
        <X className="h-3 w-3 text-muted-foreground hover:text-foreground" />
      </span>
    </Badge>
  )
}

interface ComboboxInputProps {
  placeholder?: string
  "aria-label": string
  className?: string
  id?: string
  name?: string
}

export function ComboboxInput({
  placeholder,
  "aria-label": ariaLabel,
  className,
  id,
  name,
}: ComboboxInputProps) {
  const { searchValue, setSearchValue } = useCombobox()

  return (
    <input
      type="text"
      id={id}
      name={name}
      placeholder={placeholder}
      aria-label={ariaLabel}
      value={searchValue}
      onChange={(e) => setSearchValue(e.target.value)}
      className={cn(
        "flex-1 bg-transparent outline-none placeholder:text-muted-foreground min-w-[120px]",
        className
      )}
      onClick={(e) => e.stopPropagation()}
      autoComplete="off"
    />
  )
}

interface ComboboxPopupProps {
  children: React.ReactNode
  className?: string
}

export function ComboboxPopup({ children, className }: ComboboxPopupProps) {
  return (
    <PopoverContent className={cn("w-[var(--radix-popover-trigger-width)] p-0", className)} align="start">
      <Command shouldFilter={false}>
        {children}
      </Command>
    </PopoverContent>
  )
}

interface ComboboxEmptyProps {
  children: React.ReactNode
}

export function ComboboxEmpty({ children }: ComboboxEmptyProps) {
  const { items, searchValue } = useCombobox()

  const filteredItems = items.filter((item) =>
    item.label.toLowerCase().includes(searchValue.toLowerCase())
  )

  if (filteredItems.length > 0) return null

  return <CommandEmpty>{children}</CommandEmpty>
}

interface ComboboxListProps {
  children: (item: ComboboxItem) => React.ReactNode
  className?: string
}

export function ComboboxList({ children, className }: ComboboxListProps) {
  const { items, searchValue } = useCombobox()

  const filteredItems = items.filter((item) =>
    item.label.toLowerCase().includes(searchValue.toLowerCase())
  )

  return (
    <CommandList className={className}>
      <CommandGroup>
        {filteredItems.map((item) => children(item))}
      </CommandGroup>
    </CommandList>
  )
}

interface ComboboxItemProps {
  value: ComboboxItem
  children: React.ReactNode
  className?: string
}

export function ComboboxItem({ value: itemValue, children, className }: ComboboxItemProps) {
  const { value, onValueChange, multiple, setOpen, setSearchValue } = useCombobox()

  const isSelected = React.useMemo(() => {
    if (!value) return false
    if (Array.isArray(value)) {
      return value.some((v) => v.value === itemValue.value)
    }
    return value.value === itemValue.value
  }, [value, itemValue])

  const handleSelect = () => {
    if (multiple) {
      if (Array.isArray(value)) {
        const newValue = isSelected
          ? value.filter((v) => v.value !== itemValue.value)
          : [...value, itemValue]
        onValueChange(newValue.length > 0 ? newValue : null)
      } else {
        onValueChange([itemValue])
      }
    } else {
      onValueChange(isSelected ? null : itemValue)
      setOpen(false)
    }
    setSearchValue("")
  }

  return (
    <CommandItem
      value={itemValue.value}
      onSelect={handleSelect}
      className={cn("cursor-pointer", className)}
    >
      <Check
        className={cn(
          "mr-2 h-4 w-4",
          isSelected ? "opacity-100" : "opacity-0"
        )}
      />
      {children}
    </CommandItem>
  )
}
